import { CalendarOptions } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import { Css } from "@homebound/beam";
import { ReactNode, RefObject } from "react";
import "./WrappedFullCalendar.css";

type WrappedFullCalendarProps = Pick<
  CalendarOptions,
  | "dateClick"
  | "events"
  | "eventClick"
  | "eventContent"
  | "eventResize"
  | "eventDrop"
  | "selectable"
  | "unselectAuto"
  | "unselectCancel"
  | "dayMaxEvents"
  | "editable"
> & { calendarRef: RefObject<FullCalendar>; legend?: ReactNode };

/** This is a thin wrapper around FullCalendar which standardizes blueprint specific styles/behavior for "Month" and "Week" calendar views */
export function WrappedFullCalendar(props: WrappedFullCalendarProps) {
  const {
    dateClick,
    events,
    eventClick,
    eventContent,
    eventResize,
    eventDrop,
    selectable,
    unselectAuto,
    unselectCancel,
    calendarRef,
    legend,
    dayMaxEvents,
    editable,
  } = props;

  const heightWithLegendOffset = legend ? "calc(100% - 30px)" : "100%";

  return (
    <div data-testid={"scheduleCalendarStyleContainer"} className="hb-wrapped-full-calendar" css={baseStyles}>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, ...(editable ? [interactionPlugin] : [])]}
        headerToolbar={{ start: "prev,next today", center: "title", end: "dayGridWeek,dayGridMonth" }}
        dateClick={dateClick}
        events={events}
        eventClick={eventClick}
        height={heightWithLegendOffset}
        dayCellContent={({ dayNumberText, isOther }) => {
          return (
            <div>
              <div css={Css.xs.if(isOther).gray600.else.blue700.$}>{dayNumberText}</div>
            </div>
          );
        }}
        dayHeaders={true}
        dayHeaderContent={({ text }) => {
          return <div css={Css.sm.gray700.$}>{text}</div>;
        }}
        eventContent={eventContent}
        editable={editable}
        eventResize={eventResize}
        eventDrop={eventDrop}
        eventDidMount={(element) => {
          element.el.setAttribute("data-id", element.event.id);
        }}
        selectable={selectable}
        unselectAuto={unselectAuto}
        unselectCancel={unselectCancel}
        dayMaxEvents={dayMaxEvents}
        views={{ week: { dayMaxEvents: 8 } }}
      />
      {legend && legend}
    </div>
  );
}

const baseStyles = Css.h100.bgWhite.$;
